import React from "react";
import _ from "lodash";
import IntegrationsAPI from "restAPI/IntegrationsAPI";
import { CellRenderers, Sorters } from "utils/tableUtils";
import ModalUtil from "utils/modalUtil";
import WarningPopup from "components/WarningPopup";
import ArmorCodeApiForm, { API_TOKEN_TYPE_LIST } from "./armorcodeApi/ArmorCodeApiForm";
import { getFilterProps } from "utils/tableUtils";
import TableContainer from 'components/TableContainer';
import AuthorisedSwitch from "components/AuthorisedSwitch";
import { PermissionSetMap } from "constants/RBACConstants";
import message from "components/CustomMessage";
import ActionMenuV2 from "components/ActionMenuV2";
import Vocabulary from "components/Vocabulary";
import { Link } from "react-router-dom";
import { Typography } from "antd";
import { getUrlSearch } from "utils/urlUtil";

export const ArmorCodeConfigTable = ({ data, onUpdateConfigComponent, loading, refreshData = () => {} }) => {
    // If we need it to work with the previous UI, we need to uncomment below code and add title to the table
    // function generateNewAPIKey(){
    //     ModalUtil.show({
    //         content: <ArmorCodeApiForm />,
    //         afterClose: refreshData
    //     });
    // }

    async function activateDeactivateAPIkey(record){
        const id = record.id, status = record.active;
        try{
            await IntegrationsAPI.activateDeactivateAPIkey(id, status !== true).promise;
            refreshData();
            onUpdateConfigComponent();
            message.success(`Successfully ${record.active ? "inactivated" : "activated"} token ${record.name}.`);
        }catch(e){
            console.error(e)
        } 
    }
    async function deleteAPIKey(id, name){
        if(typeof id === "undefined"){
            return;
        }
        try {
            await IntegrationsAPI.deleteAPIkey(id).promise;
            message.success(`Successfully deleted token ${name}.`);
            refreshData();
            onUpdateConfigComponent()
        } catch (e) {
            console.log(e);
        }
    }
  
    const confirmPopUp= (record)=> {
        ModalUtil.show({
        content: <WarningPopup 
            okText="Ok"
            title={`Are you sure you want to make ${record.name} token ${record.active ? "inactive" : "active"}?`} 
            onOk={()=>activateDeactivateAPIkey(record)}
            confirmText={"Confirm"}
        />
     })
   }
   
   const editDetails = (data) => {
    ModalUtil.show({
        content: <ArmorCodeApiForm configData={data}/>,
        afterClose: onUpdateConfigComponent
    })
}
   const onClick = async (_, record) => {
    switch(record.name){
      case "edit":
        editDetails(record)
        break;
      case "delete":
        await deleteAPIKey(record.data?.id, record.data?.name)
        break;
      default:
        break;
    }
  }

    return (
    <TableContainer
        // className="m-t-m"
        loading={loading}
        // title={() => <Button type="primary" onClick={generateNewAPIKey}>Generate new key</Button>}
        dataSource={data || []}
        enableScroll
        columns={[{
            title: "API Key",
            dataIndex: "initials",
            width: 120,
            alwaysDisplay: true,
            optional:true,
            default: true,
            render: (text) => "xxxx-xxxx-xxxx-" + text,
            ...getFilterProps({dataIndex: "initials", dataSource: data, onlySearch:true})
        }, {
            title: "Token Name",
            dataIndex: "name",
            width: 100,
            optional:true,
            default: true,
            ...getFilterProps({dataIndex: "name", dataSource: data}) 
        }, {
            title: <Vocabulary name="product" />,
            dataIndex: "products",
            width: 120,
            ...getFilterProps({dataIndex: "products", dataSource: data}),
            optional:true,
            default: true,
            render: (products, origianl) => {
                const productValue = products[0];
                const linkTo = _.isEmpty(productValue) ? "/product" : `/product/${productValue.id}`;
                const linkText = _.isEmpty(productValue) ? "All" : productValue.name;
                return <Link to={linkTo}>{linkText}</Link>
            },
        }, {
            title: "API Role",
            dataIndex: "role",
            width: 100,
            optional:true,
            default: true,
            render: (role) => CellRenderers.VALUE_OR_NA(role?.name),
        }, {
            title: "Last used",
            dataIndex: "lastUsedAt",
            width: 100,
            render: value => {
                const time = CellRenderers.TIME_RELATIVE(value);
                if(time === "-"){
                    return "Never";
                }
                return time;
            },
            sorter: Sorters.DATE("lastUsedAt"),
            optional:true,
        }, 
        {
            title: "Created By",
            dataIndex: ["createdBy", "name"],
            width: 100,
            sorter: Sorters.STRING("createdBy.name"),
            render: (user, record) => <Typography.Link href={`/#/user-management/users?${getUrlSearch({filters: { name: [user] }})}`}>{user}</Typography.Link>,
            optional:true,
            default: true
        }, {
            title: "Created For",
            dataIndex: ["user", "name"],
            width: 100,
            sorter: Sorters.STRING("user.name"),
            render: (user, record) => <Typography.Link href={`/#/user-management/users?${getUrlSearch({filters: { name: [user] }})}`}>{user}</Typography.Link>,
            optional:true,
            default: true
        }, {
            title: "Generated At",
            dataIndex: "createdAt",
            width: 100,
            render: CellRenderers.TIME,
            sorter: Sorters.DATE("createdAt"),
            optional:true,
        }, {
            title: "Expiry",
            dataIndex: "expiry",
            width: 100,
            render: (value) => {
                if (_.isNumber(value)) {
                    return CellRenderers.TIME_RELATIVE(value)
                    }
                return "Never";
            },
            sorter: Sorters.DATE("expiry")
        },{
            title: "Key type",
            dataIndex: "apiKeyType",
            width: 100,
            render: (apiKeyType) => _.find(API_TOKEN_TYPE_LIST, { value: apiKeyType })?.label ?? "User",
            // ...getFilterProps({dataIndex: "apiKeyType", dataSource: data})
            optional:true,
            default: true,
        }, {
            title: "Status",
            dataIndex: "active",
            width: 100,
            sorter: Sorters.NUMBER("active"),
            render:(active,record)=>{
                return (<AuthorisedSwitch permission={PermissionSetMap.EDIT_TOOL_CONFIG} checked={active} onClick={() => confirmPopUp(record)} size="small"/>)
            },
            optional:true,
            default: true,
        }, {
            title: "Account Level Access",
            dataIndex:"accountLevelToken",
            width: 100,
            render:(accountLevelToken) => accountLevelToken ? <Typography.Text>Yes</Typography.Text>: <Typography.Text>No</Typography.Text>,
            optional:true,
            default: true,
        },
        {
            title: "Action",
            dataIndex: "action",
            default: true,
            width: 100,
            alwaysDisplay: true,
            render: (active, record) => {
                
                  const items = [{
                    type: "edit"
                  },{
                    type: "delete",
                    permission: PermissionSetMap.DELETE_TOOL,
                    promptMessage: <>The token was last used at {CellRenderers.TIME(record.lastUsedAt)}, deleting it might break the tools/applications that are using this token.</>
                }]
                return (
                    <div style={{marginTop:'4px'}}>
                        <ActionMenuV2 items={items} onClick={onClick} data={record} />
                    </div>
                )
            },
        }]}
        rowKey={(record) => record.initials}
        locale={{emptyText: "No API keys generated"}}
        tableKey="armorcode-api"
        showSizeChanger
    />
)}
